import * as React from 'react';
import type { WidgetProps } from '@rjsf/utils';
import type { JSONSchema7 } from 'json-schema';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, highlightSpecialChars, drawSelection, highlightActiveLine, lineNumbers } from '@codemirror/view';
import { history, defaultKeymap, historyKeymap } from '@codemirror/commands';
import { oneDark } from '@codemirror/theme-one-dark';
import { cn } from '@shared/utils/cn';
// Optional language: we have liquid; others fall back to plain text
import { liquid } from '@codemirror/lang-liquid';
import { liquidAutocomplete } from './liquidAutocomplete';

type Props = WidgetProps & {
  className?: string;
};

/** A lightweight CodeMirror v6 widget for RJSF text-based fields (code/json). */
export default function CodeEditorWidget(props: Props) {
  const { id, value, onChange, disabled, readonly, options, placeholder, schema } = props as any;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const viewRef = React.useRef<EditorView | null>(null);

  const editableCompartment = React.useRef(new Compartment());
  const langCompartment = React.useRef(new Compartment());

  const normalizedOptions = (options ?? {}) as Record<string, any>;
  const language = normalizedOptions.language as string | undefined; // e.g. 'json', 'liquid', 'js'
  const sourceSchema = normalizedOptions.sourceSchema as JSONSchema7 | undefined; // For Liquid autocomplete
  const showLineNumbers = normalizedOptions.lineNumbers !== false;
  const heightOption = normalizedOptions.height as number | string | undefined;
  const minHeight = normalizedOptions.minHeight ?? heightOption ?? 160;
  const maxHeight = normalizedOptions.maxHeight ?? heightOption ?? undefined;
  const resolvedWidth =
    typeof normalizedOptions.width === 'number' || typeof normalizedOptions.width === 'string'
      ? normalizedOptions.width
      : undefined;

  // Build base extensions (kept stable)
  const baseExtensions = React.useMemo(
    () => [
      ...(showLineNumbers ? [lineNumbers()] : []),
      highlightSpecialChars(),
      history(),
      drawSelection(),
      highlightActiveLine(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      oneDark,
      EditorView.lineWrapping,
    ],
    [showLineNumbers],
  );

  // Hold the current language extension (can be async-loaded)
  const [langExtension, setLangExtension] = React.useState<Extension | Extension[] | null>(null);

  // Resolve language extension (supports optional dynamic import for json)
  React.useEffect(() => {
    let canceled = false;
    async function load() {
      try {
        if (language === 'liquid') {
          const ext = [liquid(), liquidAutocomplete(sourceSchema)];
          if (!canceled) setLangExtension(ext);
          return;
        }
        if (language === 'json') {
          // Dynamically import if available; otherwise fall back gracefully
          const mod = await import(/* @vite-ignore */ '@codemirror/lang-json').catch(() => null as any);
          const ext = mod?.json ? mod.json() : [];
          if (!canceled) setLangExtension(ext as any);
          return;
        }
        // Default: no language-specific syntax
        if (!canceled) setLangExtension([]);
      } catch {
        if (!canceled) setLangExtension([]);
      }
    }
    load();
    return () => {
      canceled = true;
    };
  }, [language, sourceSchema]);

  // Determine if the target schema expects non-string data to coerce from JSON text
  const wantsJsonCoercion = React.useMemo(() => {
    const t = (schema as any)?.type;
    if (typeof t === 'string') return t === 'object' || t === 'array' || t === 'number' || t === 'integer' || t === 'boolean';
    return false;
  }, [schema]);

  const stringifyValue = React.useCallback(
    (val: unknown): string => {
      if (wantsJsonCoercion) {
        try {
          if (val === undefined) return '';
          return JSON.stringify(val, null, 2);
        } catch {
          return String(val ?? '');
        }
      }
      return typeof val === 'string' ? val : val == null ? '' : String(val);
    },
    [wantsJsonCoercion],
  );

  const parseText = React.useCallback(
    (text: string): { ok: true; value: any } | { ok: false } => {
      if (!wantsJsonCoercion) return { ok: true, value: text } as any;
      try {
        const parsed = JSON.parse(text);
        // Basic type guard to avoid switching to wrong type (e.g., string)
        const t = (schema as any)?.type;
        if (t === 'array' && !Array.isArray(parsed)) return { ok: false } as const;
        if (t === 'object' && (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))) return { ok: false } as const;
        if (t === 'number' && typeof parsed !== 'number') return { ok: false } as const;
        if (t === 'integer' && (typeof parsed !== 'number' || !Number.isInteger(parsed))) return { ok: false } as const;
        if (t === 'boolean' && typeof parsed !== 'boolean') return { ok: false } as const;
        return { ok: true, value: parsed } as const;
      } catch {
        return { ok: false } as const;
      }
    },
    [schema, wantsJsonCoercion],
  );

  // Create editor
  React.useEffect(() => {
    if (!containerRef.current) return;
    const startDoc = stringifyValue(value);
    const state = EditorState.create({
      doc: startDoc,
      extensions: [
        ...baseExtensions,
        editableCompartment.current.of(EditorView.editable.of(!(disabled || readonly))),
        langCompartment.current.of(langExtension ?? []),
        EditorView.updateListener.of((v) => {
          if (v.docChanged) {
            const next = v.state.doc.toString();
            const res = parseText(next);
            if (res.ok) onChange(res.value);
          }
        }),
        EditorView.contentAttributes.of({
          'aria-label': id,
          'data-placeholder': placeholder || '',
        } as any),
      ],
    });
    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []); // init once

  // Sync external value
  React.useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    const curr = v.state.doc.toString();
    const next = stringifyValue(value);
    if (next !== curr) {
      v.dispatch({
        changes: { from: 0, to: curr.length, insert: next },
      });
    }
  }, [value, stringifyValue]);

  // Sync editable
  React.useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    v.dispatch({ effects: editableCompartment.current.reconfigure(EditorView.editable.of(!(disabled || readonly))) });
  }, [disabled, readonly]);

  // Sync language
  React.useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    v.dispatch({ effects: langCompartment.current.reconfigure(langExtension ?? []) });
  }, [langExtension]);

  return (
    <div
      id={id}
      className={cn(
        'border rounded-md shadow-xs bg-background text-foreground',
        'focus-within:ring-4 ring-ring/10 outline outline-1 outline-ring/50',
        'overflow-auto',
        props.className,
      )}
      style={{
        minHeight,
        maxHeight,
        height: heightOption,
        width: resolvedWidth,
      }}
    >
      <div
        ref={containerRef}
        className="cm"
        style={{
          minHeight,
          maxHeight,
          height: heightOption ?? 'auto',
        }}
      />
    </div>
  );
}
